import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faXmark } from '@fortawesome/free-solid-svg-icons';

import notificationApi from '@/core/notifications/api/notification-api';
import { NotificationChannel } from '@/core/notifications/model';
import { buildContactConfig, isContactFormValid } from '@/core/notifications/model/contact-form';
import ContactFormFields from './contact-form-fields';

export default function AddContactModal({
  channels,
  onClose,
  onAdded,
}: {
  channels: NotificationChannel[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [channelKey, setChannelKey] = useState(channels[0]?.key ?? '');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const channel = channels.find(c => c.key === channelKey);
  const isValid = isContactFormValid(channel, fieldValues);

  const handleChannelChange = (key: string) => {
    setChannelKey(key);
    setFieldValues({});
    setLabel('');
  };

  const handleFieldChange = (key: string, value: string) => {
    setFieldValues(v => ({ ...v, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!channel || !isValid) return;
    const config = buildContactConfig(channel, fieldValues);
    setSaving(true);
    try {
      await notificationApi.addContact(
        channel.key,
        config,
        channel.supportsLabel ? label.trim() || undefined : undefined,
      );
      toast.success('Contact added');
      onAdded();
      onClose();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error('This contact already exists for this channel');
      } else if (error?.response?.status === 422) {
        toast.error('Some required fields are missing or invalid');
      } else {
        toast.error('Could not add the contact');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered className="notifications-settings">
      <Modal.Header>
        <Modal.Title
          style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <FontAwesomeIcon icon={faUserPlus} />
          New Contact
        </Modal.Title>
        <button
          type="button"
          className="btn btn-sm text-muted"
          style={{ marginLeft: 'auto' }}
          onClick={onClose}
          title="Close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </Modal.Header>
      <Modal.Body>
        <ContactFormFields
          channels={channels}
          channelKey={channelKey}
          channel={channel}
          fieldValues={fieldValues}
          label={label}
          onChannelChange={handleChannelChange}
          onFieldChange={handleFieldChange}
          onLabelChange={setLabel}
        />
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm btn-tertiary text-base-light"
            onClick={handleSubmit}
            disabled={!isValid || saving}
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
